import OutputMonitoringValue from './OutputMonitoringValue';
import { hasOutputMonitorDisplay } from '../Utils/output_monitor';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import {
  multiOutMapping,
  multiIOMapping,
  multiIOHwTypeMappingSelect,
  multiMultiIOHwTypeMappingSelect,
  multiIOHwTypeMappping,
  multiIOInfoTypeMapping,
  funcIndexConfig,
  GetIndexMapping
} from '../Constants/constants';

import { EditableLabel } from './EditableLabel';

import { isZonable } from '../Utils/utils.js';

const useStyles = makeStyles((theme) => ({
  label: {
    color: theme.palette.primary.main
  },
  functionLabel: {
    color: theme.palette.text.primary
  }
}));

export function getMultiIOFunctionIndexLabel(controller, func, func_index, t) {
  const config =
    funcIndexConfig[func] ??
    (Number(controller?.type) === 6000 && [200, 400].includes(Number(func)) ? {} : null);
  if (!config) return null;

  const mapping = GetIndexMapping(config.mapping, controller);
  if (mapping) {
    return t(mapping[func_index] ?? mapping['default']);
  }

  const numericIndex = Number(func_index);
  return Number.isFinite(numericIndex) ? numericIndex + 1 : '---';
}

export function getMultiIOHardwareTypeLabel(controller, func, t) {
  const mappingSelect = isZonable(controller)
    ? multiMultiIOHwTypeMappingSelect
    : multiIOHwTypeMappingSelect;
  const hardwareType = multiIOHwTypeMappping[func];

  if (!hardwareType || !mappingSelect[hardwareType]) return '';

  return t(hardwareType ?? multiIOHwTypeMappping['default']);
}

export function getMultiIOCurrentLabel(info, info_type, t, monitor) {
  if (hasOutputMonitorDisplay(monitor)) return <OutputMonitoringValue monitor={monitor} />;
  if (info_type !== 1) return '';

  return `${t(
    multiIOInfoTypeMapping[info_type] ?? multiIOInfoTypeMapping['default']
  )}: ${(info / 100).toFixed(2)} A`;
}

export function MultiIOLabel({ controller, parent, func, info, info_type, monitor, ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  const hardwareTypeLabel = getMultiIOHardwareTypeLabel(controller, func, t);
  const currentLabel = getMultiIOCurrentLabel(info, info_type, t, monitor);

  if (!hardwareTypeLabel && !currentLabel) return null;

  return (
    <>
      {currentLabel && <label className={classes.label}>{currentLabel}</label>}
      {currentLabel && hardwareTypeLabel && <br />}
      {hardwareTypeLabel && <label>{hardwareTypeLabel}</label>}
    </>
  );
}

export function getMultiIOStateValue(controller, func, value, type, t) {
  // Unavailable physical inputs must never appear as digital ON or a voltage.
  // FFFF retains its established ON meaning for mixed outputs (type 4).
  if ([8, 9].includes(type) && (value === 0xffff || value === -1)) return '---';
  if (controller.type === 6000 || controller.type === 5155) {
    if ([1, 2, 8].includes(type)) {
      if (value === 0) return ' ' + t('off');
      if (value > 0) return ' ' + t('on');
      return '---';
    }
    if ([3, 9].includes(type)) {
      if (value === 0) return ' ' + t('off');
      if (value > 0) return (value / 100).toFixed(1) + ' %';
      return '--- %';
    }
    if (type === 4) {
      if (value === 0) return ' ' + t('off');
      if (value === 0xffff) return ' ' + t('on');
      if (value > 0 && value <= 1000) return (value / 100).toFixed(1) + ' %';
      return '';
    }
    return '---';
  }

  if (func !== 0 && func !== 1) {
    if (value === 0) return ' ' + t('off');
    if (value > 0) return ' ' + t('on');
    return '---';
  }
  if (value === 0) return ' ' + t('off');
  if (value > 0) return value + ' %';
  return '--- %';
}

export default function MultiOutLabel({
  controller,
  parent,
  func,
  func_index,
  value,
  type,
  ...other
}) {
  let { t } = useTranslation();
  const classes = useStyles();
  const value_field = getMultiIOStateValue(controller, func, value, type, t);

  const zonable = isZonable(controller);
  const functionName = zonable
    ? t(multiIOMapping[func] ?? multiIOMapping['default'])
    : t(multiOutMapping[func] ?? multiOutMapping['default']);

  if (zonable) {
    const functionIndex = getMultiIOFunctionIndexLabel(controller, func, func_index, t);
    return (
      <>
        <label className={classes.functionLabel} data-testid="multi-io-function">
          {functionName}
          {functionIndex !== null ? ` [${functionIndex}]` : ''}
        </label>
        {func !== 19 && (
          <>
            <br />
            <label className={classes.label} data-testid="multi-io-state">
              {value_field}
            </label>
          </>
        )}
      </>
    );
  }

  return <label className={classes.label}>{`${functionName}: ${value_field}`}</label>;
}

function MultiOutContent({ controller, parent, index, memo, multi_out }) {
  let { t } = useTranslation();
  return (
    <React.Fragment>
      <Typography gutterBottom>
        {controller.type === 6000 || controller.type === 5155
          ? t('multifunctional_io') + ' ' + index
          : t('multifunctional_out') + ' ' + index}
      </Typography>
      {multi_out != null && (
        <>
          <br />
          Type: <span style={{ fontFamily: 'monospace' }}>{multi_out.type}</span>
        </>
      )}
      {memo !== -1 && memo !== '' ? '(' + memo + ')' : <br />}
    </React.Fragment>
  );
}

export function MultiOutHeading({ controller, parent, index, memo, content, multi_out }) {
  return (
    <Typography component="div" gutterBottom>
      <MultiOutContent
        controller={controller}
        parent={parent}
        index={index}
        memo={memo}
        multi_out={multi_out}
      />
    </Typography>
  );
}

export function MultiOutHeadingEditable({
  controller,
  parent,
  index,
  displayIndex = index,
  memo,
  handleInput
}) {
  let { t } = useTranslation();
  return (
    <EditableLabel
      label={
        controller.type === 6000 || controller.type === 5155
          ? t('multifunctional_io') + ' ' + displayIndex
          : t('multifunctional_out') + ' ' + displayIndex
      }
      id={'out_' + index + '_memo'}
      value={memo}
      handleInput={handleInput}
      prefix="("
      postfix=")"
    />
  );
}
